import type { ImportResult, InvitablePerson } from './types';
import { InviteFamilyPrompt } from '../../../components/InviteFamilyPrompt';

interface ImportSummaryProps {
  result: ImportResult;
  onRetry: () => void;
  onViewEvents: () => void;
  uploadProgress: { done: number; total: number };
  importing: boolean;
  invitablePeople: InvitablePerson[];
  onInvited: (memberId: string) => void;
}

export function ImportSummary({ result, onRetry, onViewEvents, uploadProgress, importing, invitablePeople, onInvited }: ImportSummaryProps) {
  const failed = result.tasks.filter(t => t.status === 'failed');
  const succeeded = result.tasks.filter(t => t.status === 'done');

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-2xl p-6 border border-indigo-100">
        <h1 className="text-lg font-bold text-slate-900 mb-1">
          {importing
            ? `Uploading… ${uploadProgress.done}/${uploadProgress.total}`
            : failed.length === 0
              ? `${succeeded.length} photos uploaded across ${result.eventChips.length} events`
              : `${succeeded.length} of ${result.tasks.length} photos uploaded across ${result.eventChips.length} events`}
        </h1>
        {failed.length > 0 && !importing && (
          <p className="text-sm text-slate-500 mb-4">{failed.length} photo{failed.length === 1 ? '' : 's'} failed to upload — nothing else was affected.</p>
        )}

        {!importing && failed.length > 0 && (
          <div className="space-y-1.5 mb-4">
            {failed.slice(0, 10).map((task, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-700">
                <span className="truncate">{task.photo.file.name} — {task.clusterTitle}</span>
              </div>
            ))}
            {failed.length > 10 && <p className="text-xs text-slate-400">+{failed.length - 10} more</p>}
          </div>
        )}

        <div className="flex gap-2">
          {!importing && failed.length > 0 && (
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors"
            >
              Retry failed photos
            </button>
          )}
          <button
            type="button"
            onClick={onViewEvents}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
          >
            View events
          </button>
        </div>

        {failed.length > 0 && !importing && (
          <p className="text-xs text-slate-400 mt-4">
            Anything left over can be added later from an event's page with "+ Add Photo."
          </p>
        )}

        {!importing && invitablePeople.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <InviteFamilyPrompt people={invitablePeople} onInvited={onInvited} />
          </div>
        )}

        {result.eventChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-100">
            {result.eventChips.map(chip => (
              <span key={chip.id} className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                {chip.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
